import { env } from '@/env'

/* ------------------------------------------------------------------ */
/*  analytics — thin PostHog wrapper honoring consent                   */
/*  No-op in dev or when key missing                                    */
/*  See ARCHITECTURE.md §12                                             */
/* ------------------------------------------------------------------ */

let posthog: typeof import('posthog-js').default | null = null

/** Lazy-init PostHog only when key present + consent given */
async function getPostHog() {
  if (posthog) return posthog

  if (!env.POSTHOG_ENABLED || !env.isProd) return null

  // Check consent
  try {
    const raw = localStorage.getItem('hjalptorget_consent')
    if (raw) {
      const consent = JSON.parse(raw) as { analytics?: boolean }
      if (!consent.analytics) return null
    } else {
      // No consent recorded yet — don't track
      return null
    }
  } catch {
    return null
  }

  try {
    const ph = (await import('posthog-js')).default
    ph.init(env.VITE_POSTHOG_KEY!, {
      api_host: env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
      autocapture: false,
      capture_pageview: false,
      persistence: 'memory',
    })
    posthog = ph
    return ph
  } catch {
    return null
  }
}

export const analytics = {
  /** Track a named event with optional properties */
  async capture(event: string, properties?: Record<string, unknown>) {
    const ph = await getPostHog()
    ph?.capture(event, properties)
  },

  /** Identify a user (on login) */
  async identify(userId: string, traits?: Record<string, unknown>) {
    const ph = await getPostHog()
    ph?.identify(userId, traits)
  },

  /** Reset identity (on logout) */
  async reset() {
    const ph = await getPostHog()
    ph?.reset()
  },

  /** Track page view */
  async pageView(path?: string) {
    const ph = await getPostHog()
    ph?.capture('$pageview', path ? { $current_url: path } : undefined)
  },

  // ─── Customer funnel events (PROMPT-3-089) ──────────────────────

  /** Request wizard started */
  async requestStarted(categorySlug?: string) {
    return analytics.capture('request_started', { category: categorySlug })
  },

  /** Request wizard step completed */
  async stepCompleted(step: number, stepName: string) {
    return analytics.capture('request_step_completed', { step, step_name: stepName })
  },

  /** Request submitted */
  async requestSubmitted(requestId: string, categorySlug?: string) {
    return analytics.capture('request_submitted', { request_id: requestId, category: categorySlug })
  },

  /** Quote viewed by customer */
  async quoteViewed(quoteId: string, providerId: string) {
    return analytics.capture('quote_viewed', { quote_id: quoteId, provider_id: providerId })
  },

  /** Quote accepted by customer */
  async quoteAccepted(quoteId: string, providerId: string, amount?: number) {
    return analytics.capture('quote_accepted', { quote_id: quoteId, provider_id: providerId, amount })
  },

  /** Booking confirmed (payment succeeded) */
  async bookingConfirmed(bookingId: string, amount?: number) {
    return analytics.capture('booking_confirmed', { booking_id: bookingId, amount })
  },

  /** Review submitted */
  async reviewSubmitted(bookingId: string, rating: number) {
    return analytics.capture('review_submitted', { booking_id: bookingId, rating })
  },

  // ─── Provider funnel events (PROMPT-4-068) ─────────────────────

  /** Provider signup started */
  async signupStarted(role: string) {
    return analytics.capture('signup_started', { role })
  },

  /** Provider onboarding step completed */
  async onboardingStepCompleted(step: number, stepName: string) {
    return analytics.capture('onboarding_step_completed', { step, step_name: stepName })
  },

  /** Provider approved */
  async providerApproved(providerId: string) {
    return analytics.capture('provider_approved', { provider_id: providerId })
  },

  /** Provider viewed a lead */
  async leadViewed(requestId: string, categorySlug?: string) {
    return analytics.capture('lead_viewed', { request_id: requestId, category: categorySlug })
  },

  /** Provider submitted a quote */
  async quoteSubmitted(requestId: string, amount?: number) {
    return analytics.capture('quote_submitted', { request_id: requestId, amount })
  },

  /** Provider started a booking (marked in-progress) */
  async bookingStarted(bookingId: string) {
    return analytics.capture('booking_started', { booking_id: bookingId })
  },

  /** Provider completed a booking */
  async bookingCompleted(bookingId: string) {
    return analytics.capture('booking_completed', { booking_id: bookingId })
  },
}
